import { checkRateLimit, rateLimitResponse } from '~/lib/security.server'
import { useState } from 'react'
import { Form, useActionData, useNavigation } from 'react-router'
import type { MetaFunction, ActionFunctionArgs } from 'react-router'
import { redirect } from 'react-router'
import { register, createSessionCookie } from '~/lib/auth.server'
import { sendVerificationEmail } from '~/lib/email-verification.server'
import { formatPhone } from '~/hooks/use-formatter'
import { Shield, CheckCircle, ArrowRight } from 'lucide-react'

export const meta: MetaFunction = () => [
  { title: 'Inscription — AfficheServices' },
  { name: 'robots', content: 'noindex, nofollow' },
]

export async function action({ request }: ActionFunctionArgs) {
  if (!checkRateLimit(request, 'auth')) return rateLimitResponse()
  const formData = await request.formData()
  const firstName = formData.get('firstName') as string
  const lastName = formData.get('lastName') as string
  const email = formData.get('email') as string
  const password = formData.get('password') as string
  const phone = formData.get('phone') as string

  const errors: Record<string, string> = {}
  if (!firstName) errors.firstName = 'Prénom requis'
  if (!lastName) errors.lastName = 'Nom requis'
  if (!email) errors.email = 'Email requis'
  if (!password || password.length < 8) errors.password = 'Minimum 8 caractères'

  if (Object.keys(errors).length > 0) return { errors }

  const result = await register({
    email,
    password,
    firstName,
    lastName,
    phone: phone || undefined,
    role: 'homeowner',
  })

  if ('error' in result) {
    return { errors: { form: result.error } }
  }

  sendVerificationEmail(result.userId!, email, firstName).catch(console.error)

  const cookie = await createSessionCookie(result.userId!, request)
  return redirect('/tableau-de-bord', {
    headers: { 'Set-Cookie': cookie },
  })
}

export default function RegisterPage() {
  const actionData = useActionData<typeof action>()
  const navigation = useNavigation()
  const isSubmitting = navigation.state === 'submitting'
  const errors = actionData?.errors as Record<string, string> | undefined
  const [password, setPassword] = useState('')
  const [phone, setPhone] = useState('')
  const strength = password.length >= 12 ? 'Fort' : password.length >= 8 ? 'Moyen' : password.length > 0 ? 'Faible' : ''

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900">Créer un compte</h1>
      <p className="mt-1 text-sm text-slate-500">
        Recevez des devis gratuits d'artisans vérifiés.
      </p>

      {/* Quick benefits */}
      <div className="mt-4 flex flex-wrap gap-2">
        {['Gratuit', 'Sans engagement', '3 devis en 48h'].map((b) => (
          <span key={b} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold text-emerald-700">
            <CheckCircle className="h-3 w-3" /> {b}
          </span>
        ))}
      </div>

      <Form method="post" className="mt-6 space-y-4">
        {errors?.form && (
          <div className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">{errors.form}</div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="firstName" className="block text-xs font-medium text-slate-600">Prénom *</label>
            <input id="firstName" name="firstName" type="text" required placeholder="Jean"
              autoComplete="given-name"
              className="mt-1 block w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
            {errors?.firstName && <p className="mt-1 text-xs text-red-500">{errors.firstName}</p>}
          </div>
          <div>
            <label htmlFor="lastName" className="block text-xs font-medium text-slate-600">Nom *</label>
            <input id="lastName" name="lastName" type="text" required placeholder="Dupont"
              autoComplete="family-name"
              className="mt-1 block w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
          </div>
        </div>

        <div>
          <label htmlFor="email" className="block text-xs font-medium text-slate-600">Email *</label>
          <input id="email" name="email" type="email" autoComplete="email" required
            placeholder="vous@exemple.fr"
            className="mt-1 block w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
          {errors?.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
        </div>

        <div>
          <label htmlFor="phone" className="block text-xs font-medium text-slate-600">
            Téléphone <span className="text-slate-400">(optionnel)</span>
          </label>
          <input id="phone" name="phone" type="tel" autoComplete="tel" inputMode="tel"
            value={phone} onChange={(e) => setPhone(formatPhone(e.target.value))}
            placeholder="06 12 34 56 78"
            className="mt-1 block w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
          <p className="mt-0.5 text-[9px] text-slate-300">Pour être contacté(e) directement par les pros</p>
        </div>

        <div>
          <label htmlFor="password" className="block text-xs font-medium text-slate-600">Mot de passe *</label>
          <input id="password" name="password" type="password" autoComplete="new-password" required minLength={8}
            placeholder="Minimum 8 caractères"
            value={password} onChange={(e) => setPassword(e.target.value)}
            className="mt-1 block w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
          {password.length > 0 && (
            <div className="mt-1.5 flex items-center gap-2">
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                <div className={`h-full rounded-full transition-all duration-300 ${
                  password.length >= 12 ? 'w-full bg-emerald-500' :
                  password.length >= 8 ? 'w-2/3 bg-amber-500' : 'w-1/3 bg-red-400'
                }`} />
              </div>
              <span className={`text-xs font-medium ${
                password.length >= 12 ? 'text-emerald-600' : password.length >= 8 ? 'text-amber-600' : 'text-red-500'
              }`}>{strength}</span>
            </div>
          )}
          {errors?.password && <p className="mt-1 text-xs text-red-500">{errors.password}</p>}
        </div>

        <button type="submit" disabled={isSubmitting}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-hover disabled:opacity-50 btn-press">
          {isSubmitting ? (
            <><span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" /> Inscription...</>
          ) : (
            <>Créer mon compte <ArrowRight className="h-4 w-4" /></>
          )}
        </button>
      </Form>

      {/* Trust + links */}
      <div className="mt-6 space-y-3">
        <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400">
          <Shield className="h-3 w-3 text-emerald-500" /> Données protégées (RGPD) · Sans spam
        </p>
        <p className="text-center text-xs text-slate-500">
          Déjà inscrit ? <a href="/login" className="font-medium text-primary hover:underline">Se connecter</a>
        </p>
      </div>
    </div>
  )
}
